import { ArrowUpRight, MapPin } from "lucide-react";
import { Reveal } from "./Reveal";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "./ui/carousel";
import { useLanguage } from "../i18n/LanguageContext";

const FALLBACK = {
  case_superior: "https://static.wixstatic.com/media/6765ec_2bac7cf220b74abf8a0854f5700cd676~mv2.jpg/v1/fill/w_954,h_660,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/6765ec_2bac7cf220b74abf8a0854f5700cd676~mv2.jpg",
  case_oneisland: "https://images.squarespace-cdn.com/content/v1/659d10446c74f52c8b4fb736/70224e3e-3d9a-461b-a44d-193afc1eb777/1-Front-ACA+Architects-AmberByOneIslans-min.jpg",
  case_hospitality: "https://images.unsplash.com/photo-1571896349842-33c89424de2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  case_residential: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
};

const CardWrap = ({ c, i, children }) => {
  const cls = "group block bg-white border border-[#E3DED4] rounded-2xl overflow-hidden hover:border-[#1E3A5F]/40 hover:shadow-lg transition-all duration-300 h-full";
  return c.url ? (
    <a href={c.url} target="_blank" rel="noopener noreferrer" data-testid={`case-study-${i}`} className={cls}>{children}</a>
  ) : (
    <div data-testid={`case-study-${i}`} className={cls}>{children}</div>
  );
};

const arrowCls =
  "static translate-y-0 h-11 w-11 rounded-full border-[#1E3A5F]/25 bg-transparent text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-[#FBF9F5] disabled:opacity-30";

export const CaseStudies = () => {
  const { t, images } = useLanguage();
  if (!t.caseStudies) return null;

  return (
    <section className="py-24 sm:py-28 bg-white border-y border-[#E3DED4]" data-testid="case-studies-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Carousel opts={{ align: "start" }} className="w-full">
          <div className="flex items-end justify-between gap-6">
            <Reveal className="max-w-2xl">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#8C6B4F] font-semibold">{t.caseStudies.eyebrow}</p>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#142D44] leading-tight">{t.caseStudies.title}</h2>
              <p className="mt-5 text-[#4A5568] leading-relaxed">{t.caseStudies.sub}</p>
            </Reveal>
            <div className="hidden sm:flex lg:hidden items-center gap-3 shrink-0 pb-1">
              <CarouselPrevious className={arrowCls} data-testid="case-studies-prev-button" />
              <CarouselNext className={arrowCls} data-testid="case-studies-next-button" />
            </div>
          </div>
          <Reveal>
            <CarouselContent className="mt-14 -ml-8">
              {t.caseStudies.items.map((c, i) => (
                <CarouselItem key={c.name} className="pl-8 basis-[88%] sm:basis-[60%] lg:basis-1/3">
                  <CardWrap c={c} i={i}>
                    <div className="aspect-[16/10] overflow-hidden relative">
                      <img src={images[c.img] || FALLBACK[c.img]} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <span className="absolute top-4 left-4 bg-[#142D44]/90 text-[#FBF9F5] font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-md">{c.tag}</span>
                    </div>
                    <div className="p-7">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-serif text-2xl text-[#142D44] leading-snug group-hover:text-[#B85D2A] transition-colors duration-300">{c.name}</h3>
                          <p className="mt-2 flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.14em] text-[#8C6B4F]">
                            <MapPin size={12} strokeWidth={1.5} /> {c.location}
                          </p>
                        </div>
                        {c.url && <ArrowUpRight size={22} className="text-[#B85D2A] shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.25} />}
                      </div>
                      <p className="mt-4 text-sm text-[#4A5568] leading-relaxed">{c.desc}</p>
                      {c.url && <p className="mt-5 text-xs font-semibold text-[#B85D2A] uppercase tracking-[0.12em]">{t.caseStudies.visit}</p>}
                    </div>
                  </CardWrap>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Reveal>
          <div className="mt-8 flex sm:hidden items-center justify-center gap-3">
            <CarouselPrevious className={arrowCls} data-testid="case-studies-prev-button-mobile" />
            <CarouselNext className={arrowCls} data-testid="case-studies-next-button-mobile" />
          </div>
        </Carousel>
      </div>
    </section>
  );
};
